import * as React from "react";
import { useState } from "react";
import { AppLogo } from "../../../../../../Shared/Process/AppLogo";
import {
    consolidatedAccountDashboardTabEnum,
    dashboardRelatedPageEnum,
    portfolioDashboardTabEnum,
    portfolioDashboardTabPresentationEnum,
    prospectDashboardTabEnum,
    prospectDashboardTabPresentationEnum,
} from "../..../../../Resource/Content";
import { Tab } from "../../../../../../Shared/Tab/Tab";
import { UserClaim } from "../../../../../../../Wrap/Constants/Enums/UserClaim";
import { SlideNavigator } from "./SlideNavigator";
import { Lightbox } from "../../../../../../../react-core/Html/Components/Lightbox";

interface IPresentationContext {
    isPresentationView: boolean;
    togglePresentationView: () => void;
}

export const PresentationContext = React.createContext<IPresentationContext>({
    isPresentationView: false,
    togglePresentationView: () => {},
});

const tabPermission = (): Map<portfolioDashboardTabEnum, UserClaim | UserClaim[]> => {
    const permission = new Map<portfolioDashboardTabEnum, UserClaim | UserClaim[]>();
    permission.set(portfolioDashboardTabEnum.Summary, UserClaim.AdviserPortalPerformanceSummaryCanView);
    permission.set(portfolioDashboardTabEnum.Investments, UserClaim.AdviserPortalClientInvestmentsCanView);
    permission.set(portfolioDashboardTabEnum.Insights, UserClaim.AdviserPortalPerformanceSummaryCanView);
    permission.set(portfolioDashboardTabEnum.Performance, [
        UserClaim.AdviserPortalPerformanceSummaryCanView,
        UserClaim.AdviserPortalPerformanceDetailCanView,
        UserClaim.AdviserPortalRealisedGainLossCanView,
        UserClaim.AdviserPortalUnrealisedGainLossCanView,
        UserClaim.AdviserPortalPerformanceIncomeCanView,
        UserClaim.AdviserPortalPerformanceChargesCanView,
    ]);
    permission.set(portfolioDashboardTabEnum.Transactions, [
        UserClaim.AdviserPortalInvestmentTransactionCanView,
        UserClaim.AdviserPortalPaymentsAndWithdrawlsCanView,
        UserClaim.AdviserPortalAccountTransfersCanView,
        UserClaim.AdviserPortalDividendSummaryCanView,
        UserClaim.AdviserPortalOtherCorporateActionsCanView,
        UserClaim.AdviserPortalPerformanceChargesCanView,
    ]);
    permission.set(portfolioDashboardTabEnum.Documents, UserClaim.AdviserPortalDocumentLibraryCanView);
    permission.set(portfolioDashboardTabEnum.Roles, UserClaim.AdviserPortalCustomerPartialAccessCanView);
    permission.set(portfolioDashboardTabEnum.Activity, UserClaim.AdviserPortalCustomerPartialAccessCanView);
    permission.set(portfolioDashboardTabEnum.ESG, UserClaim.AdviserPortalEsgAnalyserCanView);
    return permission;
};

interface IPresentationViewWrapperProps {
    setParentState: any;
    header: React.ReactChild | React.ReactChildren;
    children: React.ReactChild | React.ReactChildren;
    tabs: any;
    tabIndex:
        | portfolioDashboardTabEnum
        | dashboardRelatedPageEnum
        | prospectDashboardTabEnum
        | consolidatedAccountDashboardTabEnum;
    onTabClick: (tab: number) => void;
}

export const PresentationViewWrapper = (props: IPresentationViewWrapperProps) => {
    const { children, header, tabs, tabIndex, onTabClick } = props;
    const [isPresentationView, setIsPresentationView] = useState(false);

    const togglePresentationView = () => {
        setIsPresentationView(!isPresentationView);
        props.setParentState({ isPresentationView: !isPresentationView });
    };

    let filteredTabs = tabs;
    if (isPresentationView) {
        if (tabs === portfolioDashboardTabEnum) {filteredTabs = portfolioDashboardTabPresentationEnum;}
        if (tabs === prospectDashboardTabEnum) {filteredTabs = prospectDashboardTabPresentationEnum;}
    }

    const tabNav = tabs ? (
        <Tab permission={tabPermission()} tabs={filteredTabs} curTabIndex={tabIndex} onClick={onTabClick} />
    ) : null;

    return (
        <>
            {isPresentationView && (
                <Lightbox
                    show={isPresentationView}
                    cssWrapper="lightbox-fullscreen"
                    title="Fullscreen"
                    onClose={() => {
                        setIsPresentationView(false);
                        props.setParentState({ isPresentationView: false });
                    }}
                >
                    {isPresentationView && (
                        <>
                            <div className="lightbox-fullscreen-header">
                                <div className="container size-fluid">
                                    <AppLogo />
                                </div>
                            </div>
                            <div className="lightbox-fullscreen-panel">
                                <div className="container size-1600 pb-20">
                                    <PresentationContext.Provider
                                        value={{ isPresentationView, togglePresentationView }}
                                    >
                                        {header}
                                        {tabNav}
                                        {children}
                                        <SlideNavigator tabs={filteredTabs} tabIndex={tabIndex} onClick={onTabClick} />
                                    </PresentationContext.Provider>
                                </div>
                            </div>
                        </>
                    )}
                </Lightbox>
            )}
            {!isPresentationView && (
                <PresentationContext.Provider value={{ isPresentationView, togglePresentationView }}>
                    {header}
                    {tabNav}
                    {children}
                </PresentationContext.Provider>
            )}
        </>
    );
};
